import {
  Activity,
  ArrowLeftRight,
  Banknote,
  BarChart3,
  BookOpen,
  Boxes,
  ClipboardList,
  CreditCard,
  FileSpreadsheet,
  Gift,
  HandCoins,
  Headset,
  LayoutGrid,
  LifeBuoy,
  ListChecks,
  type LucideIcon,
  Monitor,
  Package,
  PackageSearch,
  Receipt,
  Scale,
  RotateCcw,
  Settings,
  ShoppingCart,
  Smartphone,
  Tags,
  Truck,
  Users,
  MapPin,
} from 'lucide-react';

/**
 * Information architecture.
 *
 * Grouped by the operator's job, not by the database. Someone working the
 * floor thinks "I'm dealing with a sale", "I'm dealing with stock",
 * "I'm dealing with a supplier" — the groups below are those jobs. Every
 * screen in the console hangs off exactly one of them, so the rail is a
 * complete map of the product with nothing hidden in a submenu-only path.
 */

export interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  /** Shown in the command palette and ⌘K search as extra match terms. */
  keywords?: string[];
  /** Minimum role for the screen to appear. Server still enforces. */
  minRole?: 'manager' | 'admin';
  /** Key of a live counter from the badge feed, if this item carries one. */
  badge?: 'ordersOpen' | 'returnsPending' | 'costApprovals' | 'quarantine' | 'lowStock';
  /**
   * Where the counter itself leads, when that is not the item's own screen.
   * The rail renders the count as a separate link to this path.
   */
  badgeTo?: string;
  /**
   * Other path prefixes that belong to this item's screen, so the rail lights it
   * up there too (see `activeRailItem`).
   */
  activeOn?: string[];
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

export const NAV: NavGroup[] = [
  {
    label: 'Overview',
    items: [
      {
        label: 'Today',
        to: '/',
        icon: LayoutGrid,
        keywords: ['dashboard', 'home', 'overview', 'sales today'],
      },
      {
        label: 'Lookup',
        to: '/lookup',
        icon: PackageSearch,
        keywords: ['scan', 'barcode', 'price check', 'find'],
      },
    ],
  },
  {
    label: 'Selling',
    items: [
      {
        label: 'Transactions',
        to: '/transactions',
        icon: Receipt,
        keywords: ['sales', 'receipts', 'tickets', 'voids'],
      },
      {
        label: 'Orders',
        to: '/orders',
        icon: ShoppingCart,
        keywords: ['pickup', 'delivery', 'picking', 'campaign'],
        badge: 'ordersOpen',
      },
      {
        label: 'Returns',
        to: '/returns',
        icon: RotateCcw,
        keywords: ['refunds', 'review', 'approve return'],
        badge: 'returnsPending',
      },
      {
        label: 'Parked carts',
        to: '/recovery',
        icon: LifeBuoy,
        keywords: ['recovery', 'stuck', 'suspended', 'bridge timeout', 'payment pending'],
        minRole: 'manager',
      },
      {
        label: 'Assist',
        to: '/assist',
        icon: Headset,
        keywords: ['help', 'lane', 'office', 'price check', 'override', 'unknown item'],
        // The hub itself requires supervisor+; `minRole` only speaks manager /
        // admin, so the screen states the requirement instead of hiding.
      },
      {
        label: 'Cash',
        to: '/cash',
        icon: Banknote,
        keywords: ['drawer', 'shift', 'count', 'variance', 'float'],
      },
    ],
  },
  {
    label: 'Catalog',
    items: [
      {
        label: 'Products',
        to: '/products',
        icon: Package,
        keywords: ['items', 'sku', 'catalog', 'plu'],
      },
      {
        label: 'Families',
        to: '/products/families',
        icon: Tags,
        keywords: ['family', 'chicken', 'beef', 'input products', 'sheet', 'fresh'],
      },
      {
        label: 'Pricing',
        to: '/pricing',
        icon: Tags,
        keywords: ['promotions', 'sale price', 'bogo', 'discount'],
      },
      {
        label: 'Organize',
        to: '/organize',
        icon: Boxes,
        keywords: ['departments', 'brands', 'zones', 'barcodes', 'labels'],
      },
    ],
  },
  {
    label: 'Stock',
    items: [
      {
        label: 'Inventory',
        to: '/inventory',
        icon: ClipboardList,
        keywords: ['on hand', 'count', 'adjust', 'lots', 'expiring', 'low stock'],
        badge: 'lowStock',
      },
    ],
  },
  {
    // UX-1 (TASK-0240): buying from vendors is its own job. The order guide,
    // invoices, cost approvals and vendor catalogs are tabs inside Purchasing
    // (features/buying/purchasing-tabs.ts), and ⌘K still finds each by name.
    label: 'Buying',
    items: [
      {
        label: 'Purchasing',
        to: '/purchasing',
        icon: Truck,
        keywords: [
          'purchase order',
          'po',
          'receive',
          'invoice',
          'order guide',
          'cost approvals',
          'catalogs',
        ],
        // The pending cost changes live on their own queue, not the PO list.
        badge: 'costApprovals',
        badgeTo: '/vendors/cost-approvals',
        // Its tabs that still live under /vendors (UX-2 moves the URLs).
        activeOn: ['/vendors/cost-approvals', '/vendors/catalogs'],
      },
      {
        label: 'Vendors',
        to: '/vendors',
        icon: ArrowLeftRight,
        keywords: ['suppliers', 'cost', 'vendor items'],
      },
    ],
  },
  {
    label: 'People',
    items: [
      {
        label: 'Customers',
        to: '/customers',
        icon: Users,
        keywords: ['house account', 'balance', 'ledger', 'charge'],
      },
      {
        label: 'Gift cards',
        to: '/gift-cards',
        icon: Gift,
        keywords: ['giftcard', 'batch', 'balance', 'activate'],
      },
      {
        label: 'Account credits',
        to: '/account-credits',
        icon: HandCoins,
        keywords: [
          'credit',
          'bonus',
          'allocate',
          'sponsor',
          'csv',
          'gift refund',
          'transfer',
        ],
        minRole: 'manager',
      },
    ],
  },
  {
    label: 'Insight',
    items: [
      {
        label: 'Reports',
        to: '/reports',
        icon: BarChart3,
        keywords: ['sales report', 'financial', 'cashier', 'product', 'inventory report'],
      },
    ],
  },
  {
    label: 'System',
    items: [
      {
        label: 'Registers',
        to: '/system/registers',
        icon: Monitor,
        keywords: ['lanes', 'stations', 'heartbeat', 'enrollment', 'approve'],
      },
      {
        label: 'Staff',
        to: '/system/staff',
        icon: CreditCard,
        keywords: ['users', 'cashiers', 'roles', 'pin'],
      },
      {
        label: 'Sync health',
        to: '/system/sync',
        icon: Activity,
        keywords: ['quarantine', 'dead letter', 'queue depth', 'replay'],
        badge: 'quarantine',
        minRole: 'manager',
      },
      {
        label: 'Settings',
        to: '/settings',
        icon: Settings,
        keywords: ['config', 'store', 'receipt', 'whatsapp', 'quick panel'],
      },
      {
        label: 'Floor devices',
        to: '/system/floor-devices',
        icon: Smartphone,
        keywords: [
          'android',
          'handheld',
          'phone',
          'pairing code',
          'apk',
          'install',
          'approve',
        ],
        // Manager+ on the server; the screen explains the requirement rather
        // than hiding (the Assist / Tracking-tab pattern).
      },
    ],
  },
];

/** Flat list, for the command palette. */
export const NAV_ITEMS: NavItem[] = NAV.flatMap((g) => g.items);

function matchesPrefix(pathname: string, prefix: string): boolean {
  return prefix === '/'
    ? pathname === '/'
    : pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/**
 * The one rail item that is current on `pathname`: the item whose own path or
 * `activeOn` prefix is the longest match. On /vendors/cost-approvals that is
 * Purchasing, not Vendors (whose /vendors is a shorter prefix of it); on
 * /vendors/3 it is Vendors. Today (`/`) only matches exactly.
 */
export function activeRailItem(
  items: readonly NavItem[],
  pathname: string
): NavItem | null {
  let best: NavItem | null = null;
  let bestLength = -1;
  for (const item of items) {
    for (const prefix of [item.to, ...(item.activeOn ?? [])]) {
      if (matchesPrefix(pathname, prefix) && prefix.length > bestLength) {
        best = item;
        bestLength = prefix.length;
      }
    }
  }
  return best;
}

/**
 * Screens reached from another screen rather than the rail, listed in the
 * command palette so a search finds them directly.
 */
export const PALETTE_ONLY_ITEMS: NavItem[] = [
  // UX-1 (TASK-0240): Purchasing tabs, out of the rail but still found by name.
  {
    label: 'Order guide',
    to: '/purchasing/guide',
    icon: ListChecks,
    keywords: ['weekly', 'order', 'reorder', 'buy', 'guide'],
  },
  {
    label: 'Vendor catalogs',
    to: '/vendors/catalogs',
    icon: BookOpen,
    keywords: ['catalog', 'price list', 'offers', 'who sells', 'import catalog'],
  },
  {
    label: 'Cost approvals',
    to: '/vendors/cost-approvals',
    icon: Scale,
    keywords: ['cost changes', 'approve cost', 'pending costs', 'costs'],
  },
  {
    // Opens Catalogs & price lists until price lists get their own switch (INC-12e).
    label: 'Price list',
    to: '/vendors/catalogs',
    icon: FileSpreadsheet,
    keywords: ['price lists', 'vendor prices', 'import price list'],
  },
  {
    // TASK-0256 (DROP-1): reached from the Vendors header and How we order, and found here.
    label: 'Drop-off locations',
    to: '/vendors/drop-off-locations',
    icon: MapPin,
    keywords: ['drop-off', 'drop off', 'warehouse', 'backhaul', 'ship to', 'deliver to'],
  },
  {
    // TASK-0257 (DROP-2a): Purchasing › Freight.
    label: 'Freight',
    to: '/purchasing/freight',
    icon: Truck,
    keywords: [
      'freight',
      'trips',
      'backhaul',
      'docking',
      'drop-off charges',
      'cost with freight',
      'landed cost',
    ],
  },
  {
    label: 'Label templates',
    to: '/settings/label-templates',
    icon: Tags,
    keywords: ['label design', 'shelf tag', 'price tag', 'sticker', 'label station'],
  },
];
